import { observer } from "mobx-react";
import * as React from "react";
import { validateInput } from "../../../actions/validateInput";
import FloatingLabel from "../../../components/comp/Form/FloatingLabel";
import { store as uiStore } from "../../../store/ui";
import { InputProps } from "../../../interface";

@observer
class Input extends React.Component<InputProps & React.InputHTMLAttributes<HTMLInputElement>> {
    public state = { error: "" };

    public runValidation(value: string, norefresh = false): string | null {
        const { stateName, validation, handleValidationError, enforced } = this.props;
        if (validation) {
            const errMsg = validateInput(value ? String(value) : value, validation);
            if (errMsg !== this.state.error && !enforced) {
                if (!norefresh) {
                    this.setState({ error: errMsg });
                }
                if (uiStore && uiStore.loginForm) {
                    if (!norefresh) {
                        const loginForm = uiStore.loginForm;
                        loginForm[`_error_${stateName || ""}`] = errMsg;
                        uiStore.setLoginForm(loginForm);
                    }
                }
                if (handleValidationError) {
                    handleValidationError(errMsg);
                }
            }
            return errMsg;
        }
        return null;
    }

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { digitGrouping, context, stateName, onChange, afterChanged, enforced } = this.props;
        const value = digitGrouping ? e.target.value.replace(/,/g, "") : e.target.value;
        if (!onChange || onChange(e) !== false) {
            const errMsg = this.runValidation(value);
            if (!enforced || (enforced && errMsg === "")) {
                if (context && stateName) {
                    if (uiStore.loginForm) {
                        uiStore.setLoginForm({
                            ...uiStore.loginForm,
                            [stateName]: value
                        });
                    } else {
                        context[stateName] = value;
                        this.setState({});
                    }
                }
            }
        }
        if (afterChanged != null) {
            afterChanged();
        }
    };

    public componentDidMount(): void {
        const { stateName, defaultValue } = this.props;
        if (defaultValue && stateName && typeof uiStore.loginForm[stateName] !== "string") {
            const loginForm = uiStore.loginForm;
            loginForm[stateName] = defaultValue;
            uiStore.setLoginForm({
                ...uiStore.loginForm,
                [stateName]: defaultValue
            });
        }

        let value = "";
        if (stateName) {
            value = String(uiStore.loginForm[stateName]);
        }
        this.runValidation(value);
    }

    public render(): React.ReactElement {
        let { id, context, stateName, title, tooltip, error, value, holderStyle, noError, refresh, suffix, required, placeholder, ...other } =
            this.props;
        if (context && stateName) {
            value = String(uiStore.loginForm[stateName]);
        }

        const { digitGrouping } = this.props;

        const errMsg = this.runValidation(String(value || ""), refresh ? false : true);
        const err = error || errMsg || "";

        // digitGrouping is the identifier that the input field is for number types
        // decimal limitation fixed below:
        // 1. Trailing dots
        // 2. 0s are automatically trimmed -> inputting '1.10' automatically evaluated to '1.1'
        // 3. Limit decimal values to 12 digits (New implementation)
        let renderedValue = value;
        // if input type is number (known from digitGrouping), perform pre-processing
        if (digitGrouping) {
            const valueString = String(value);
            const parsedValue = parseFloat(valueString);
            const hasDecimal = valueString.includes(".");
            const isTrailingDecimal = valueString.match(/^\d+\.+$/);
            const [integerPart, fractionalPart] = hasDecimal ? valueString.split(".") : [valueString, ""];
            const parsedIntegerPart = parseInt(integerPart, 10);
            const parsedFractionalPart = parseInt(fractionalPart, 10);
            const valueIsNumber = !isNaN(parsedIntegerPart) && (!isNaN(parsedFractionalPart) || fractionalPart === "");
            const leadingFractionalZeroes = fractionalPart.match(/^(0+).+$/)?.[1] || "";
            if (valueIsNumber) {
                if (isTrailingDecimal) {
                    renderedValue = parsedIntegerPart.toLocaleString() + ".";
                } else if (hasDecimal) {
                    renderedValue =
                        parsedIntegerPart.toLocaleString() + "." + (leadingFractionalZeroes + parsedFractionalPart?.toString()).substr(0, 12);
                } else {
                    renderedValue = parsedIntegerPart.toLocaleString();
                }
            } else {
                renderedValue = isNaN(parsedValue) ? "" : parsedValue.toLocaleString() + "";
            }
        }

        return (
            <div className={`input-holder ${other.flex ? `flex-${other.flex}` : ""} ${holderStyle}`} {...other}>
                {(title || tooltip) && (
                    <div>
                        {title && <FloatingLabel text={title} required={required} />}
                        {tooltip && (
                            <div title={tooltip} className={"material-icons"}>
                                help
                            </div>
                        )}
                    </div>
                )}
                <div>
                    <input id={id} {...other} value={renderedValue || ""} onChange={this.onChange} placeholder={placeholder} />
                    {suffix && <span>{suffix}</span>}
                </div>
                {!noError && err && <div className={`error ${err.startsWith("++") ? "success" : "error"}`}>{err.replace("++", "")}</div>}
            </div>
        );
    }
}

export { Input };
